<template>
  <div v-if="marche">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3"> {{ getTitle }} </h1>
              <app-breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button" @click="$router.go(-1)">
              {{ $t('general.button.cancel')}}
            </button>
            <button class="button is-addOrEdit" @click="save()">
              {{ $t('general.button.sauvegarder') }}
            </button>
            <piece-jointe-button
              class="mgr-2"
              :perm="permMarche.maj">
            </piece-jointe-button>
          </div>
        </div>
      </div>
    </div>
    <error-msg-component
      v-if="error"
      :messages="messages"
      :error="error"
      :champs-vide="champsVide"
      :messages-champs-vide="messagesChampsVide"
      @close="error = $event"
      class="mgt-20px"
      title="general.problemOccurred"
      withEmplyFields/>
    <div class="tab">
      <ul>
        <li
          v-for="(tab, index) in tabs" :key="index"
          :class="{'is-active': activeTab === tab}"
          @click="activeTab = tab">
          {{ $t(`addEditMarche.tabs.${tab}`) }}
        </li>
      </ul>
    </div>
    <section class="corps">
      <component
        :is="activeTab"
        :detail="false"
        :marche="marche"/>
    </section>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { get, create, update } from '@/api/achat/marche';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import validationGeneral from '@/services/validation/validation';
import genererMarche from '@/services/achat/marche/genererMarche';
import { postGetData, preSaveOrUpdateData } from '@/services/achat/marche/formatageMarche';
import translate from '@/services/achat/marche/translatorMarche';
import validationMarche from '@/services/achat/marche/validationMarche';
import AppBreadcrumb from '@/components/general/breadcrumb';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import Informations from '@/components/achat/marche/informations';
import Produits from '@/components/achat/marche/produits';
import fournisseurs from '@/components/achat/marche/fournisseurs/listeFournisseur';
import { MARCHE } from '@/configuration/achat/permissionsConf';
import pieceJointeButton from '@/components/general/button/pieceJointeButton';

const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');

export default {
  components: {
    AppBreadcrumb,
    errorMsgComponent,
    Informations,
    Produits,
    fournisseurs,
    pieceJointeButton,
  },
  data() {
    return {
      marche: null,
      error: false,
      messages: [],
      champsVide: [],
      messagesChampsVide: [],
      libelle: '',
      activeTab: 'informations',
      permMarche: MARCHE,
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    tabs() {
      return ['informations', 'produits', 'fournisseurs'];
    },
    getTitle() {
      let title = this.dataAlreadyExist
        ? this.$t('addEditMarche.title.edit')
        : this.$t('addEditMarche.title.add');
      if (this.dataAlreadyExist) title += ' ' + this.libelle;
      return title;
    },
    dataAlreadyExist() {
      return this.marche && !!(this.marche.id);
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('addEditMarche.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('addEditMarche.breadcrumb.liste'), route: { name: 'l.presse.marche' }, isActive: false },
        { title: this.dataAlreadyExist ? this.$i18n.t('addEditMarche.breadcrumb.edit') : this.$i18n.t('addEditMarche.breadcrumb.add'), route: { name: '' }, isActive: true },
      ];
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
      insertAttachments: 'insert',
      updateAttachments: 'update',
    }),
    async save() {
      this.error = false;
      this.messages = [];
      this.messagesAvertissement = [];
      this.messagesChampsVide = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        let marche = cloneDeep(this.marche);
        marche.societeId = this.societeActiveId;
        preSaveOrUpdateData(marche);
        if (!this.dataAlreadyExist) {
          marche = (await create(marche)).data.result;
          await this.insertAttachments(marche.id);
          messageSucces(this.$i18n.t('addEditMarche.notifications.successCreation'));
        } else {
          marche = (await update(marche)).data.result;
          await this.updateAttachments(marche.id);
          messageSucces(this.$i18n.t('addEditMarche.notifications.successChange'));
        }
        await this.setAttachmentElementId({ id: marche.id });
        await this.$router.push({ name: 'c.presse.marche', params: { id: marche.id } });
        loadingComponent.close();
      } catch (e) {
        loadingComponent.close();
        if (e.response && e.response.data.error) {
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translate);
          validationMarche(e.response, this.messages, this.$i18n, translate);
          this.error = true;
          return;
        }
        throw e;
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'Marche' });
      let marche = genererMarche();
      if (this.$route.params.id) {
        marche = (await get(this.$route.params.id)).data.result;
        this.libelle = marche.libelle;
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.libelle }));
        await this.setAttachmentElementId({ id: marche.id });
      } else {
        this.setInformationPage(this.$t('pagePresse.marche.creation'));
      }
      this.marche = postGetData(marche);
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>
